<script setup lang="ts">
import { useSerialize } from "@lxc/app-device-common";
import type { RuleItem } from "async-validator/dist-types/interface";
import { useAcl } from "vue-simple-acl";
import { ACLRoles } from "~/core/models/ACLRoles.enum";
import LxcError from "~/core/models/LxcError.class";
import {
  NotificationKey,
  showNotificationSuccess,
} from "~/core/utils/notifications";
import { useActivateFirmware } from "~/modules/firmware/composables/useActivateFirmware";
import { useDeactivateFirmware } from "~/modules/firmware/composables/useDeactivateFirmware";
import { useDeleteFirmware } from "~/modules/firmware/composables/useDeleteFirmware";
import { useGetFirmware } from "~/modules/firmware/composables/useGetFirmware";
import { useUpdateFirmwareNameAndDescription } from "~/modules/firmware/composables/useUpdateFirmwareNameAndDescription";
import type { Firmware } from "~/modules/firmware/models/Firmware.interface";
import { FirmwareStatus } from "~/modules/firmware/models/FirmwareStatus.enum";
import type { FirmwareUpdatePayload } from "~/modules/firmware/models/FirmwareUpdatePayload.interface";
import ILxcLightRotateCw from "~icons/lxc-light/rotate-cw";
import ILxcLightSlash from "~icons/lxc-light/slash";
import ILxcLightTrash2 from "~icons/lxc-light/trash-2";

const props = defineProps<{
  businessUnitCode?: number;
  organizationCode?: number;
  uuid?: string;
}>();
const emit = defineEmits(["close", "delete"]);

const show = ref(false);

const { t } = useI18n();
const serialize = useSerialize();
const { can } = useAcl();

const {
  isLoading,
  error,
  getFirmware: getFirmwareDetails,
  result,
} = useGetFirmware({
  businessUnitCode: props.businessUnitCode,
  organizationCode: props.organizationCode,
});

const [deleteConfirmDialogVisible, toggleDeleteConfirmVisible] = useToggle();

const {
  error: errorDeleteFirmware,
  deleteFirmware,
  isLoading: isLoadingDeleteFirmware,
} = useDeleteFirmware({ businessUnitCode: props.businessUnitCode });

const [activateConfirmDialogVisible, toggleActivateConfirmVisible] =
  useToggle();

const {
  activateFirmware,
  error: errorActivateFirmware,
  isLoading: isLoadingActivateFirmware,
} = useActivateFirmware({ businessUnitCode: props.businessUnitCode });

const [deactivateConfirmDialogVisible, toggleDeactivateConfirmVisible] =
  useToggle();

const {
  deactivateFirmware,
  error: errorDeactivateFirmware,
  isLoading: isLoadingDeactivateFirmware,
} = useDeactivateFirmware({ businessUnitCode: props.businessUnitCode });

const {
  error: errorUpdateFirmwareNameAndDescription,
  updateFirmwareNameAndDescription,
} = useUpdateFirmwareNameAndDescription({
  businessUnitCode: props.businessUnitCode,
});

const edited: Ref<boolean> = ref(false);
const defaultFirmwareData: Firmware = {
  uuid: "",
  name: "",
  description: "",
  version: "",
  range: "",
  rangeFriendlyName: "",
  declination: "",
  hardwareVersions: [],
  firmwareVersions: [],
};
const firmwareData = reactive<Firmware>(Object.assign({}, defaultFirmwareData));
let initialFirmwareData: string = serialize(defaultFirmwareData);

const firmwareNameUpdateFormRules: RuleItem[] = [
  { required: true, message: t("input.error.required") },
];

const firmwareName: Ref<string | undefined> = ref();
const isEditingName = ref(false);
const isSavingName = ref(false);

const firmwareDescription: Ref<string | undefined> = ref();
const isEditingDescription = ref(false);
const isSavingDescription = ref(false);

function open() {
  show.value = true;
}

function close() {
  show.value = false;
  isEditingName.value = false;
  isEditingDescription.value = false;
  emit("close", {
    edited: edited.value,
  });
}

const activateFirmwareVisible = computed(
  () => firmwareData?.status === FirmwareStatus.DEACTIVATED,
);
const deactivateFirmwareVisible = computed(
  () => firmwareData?.status === FirmwareStatus.ACTIVATED,
);
const canUpdateFirmware: ComputedRef<boolean> = computed(
  () => can(ACLRoles.CAN_WRITE_FEATURE) && !!props.businessUnitCode,
);

const sectionClass = "border-solid border-b border-gray-300 pb-6 mt-6";
const labelClass = "text-gray-500 mb-3 mt-4";
const valueClass = "text-base font-medium text-gray-700";

function setFirmwareForm(paramFirmwareData?: Firmware) {
  Object.assign(firmwareData, paramFirmwareData || defaultFirmwareData);
  firmwareName.value = firmwareData.name;
  firmwareDescription.value = firmwareData.description;
  initialFirmwareData = serialize(firmwareData);
}

const getFirmwareByUuid = async () => {
  error.value = null;

  if (props.uuid) {
    open();

    await getFirmwareDetails(props.uuid);

    setFirmwareForm(result.value);
  } else {
    close();
  }
};

const updateEdited = (firmware?: Firmware) => {
  edited.value = serialize(firmware ?? {}) !== initialFirmwareData;
};

const onDeleteFirmwareAndPropagateChange = async () => {
  toggleDeleteConfirmVisible(false);

  await deleteFirmware(firmwareData.uuid);

  if (errorDeleteFirmware.value) {
    (errorDeleteFirmware.value as LxcError).notify(NotificationKey.removeError);
  } else {
    showNotificationSuccess(t(NotificationKey.removeSuccess));
    emit("delete");
  }
};

const confirmActivateFirmware = async () => {
  if (firmwareData.uuid) {
    toggleActivateConfirmVisible();
  }
};

const confirmDeactivateFirmware = async () => {
  if (firmwareData.uuid) {
    toggleDeactivateConfirmVisible();
  }
};

const onActivateFirmware = async () => {
  toggleActivateConfirmVisible(false);

  await activateFirmware(firmwareData.uuid);

  if (LxcError.check(errorActivateFirmware.value)) {
    (errorActivateFirmware.value as LxcError).notify(NotificationKey.saveError);
  } else {
    showNotificationSuccess(t(NotificationKey.saveSuccess));
    firmwareData.status = FirmwareStatus.ACTIVATED;
  }
};

const onDeactivateFirmware = async () => {
  toggleDeactivateConfirmVisible(false);

  await deactivateFirmware(firmwareData.uuid);

  if (LxcError.check(errorDeactivateFirmware.value)) {
    (errorDeactivateFirmware.value as LxcError).notify(
      NotificationKey.saveError,
    );
  } else {
    showNotificationSuccess(t(NotificationKey.saveSuccess));
    firmwareData.status = FirmwareStatus.DEACTIVATED;
  }
};

const onValidateEditName = async (newValue: string) => {
  const updateNamePayload: FirmwareUpdatePayload = {
    name: newValue,
  };
  isSavingName.value = true;
  const isUpdateSucceed = await onValidate(updateNamePayload);
  if (isUpdateSucceed) {
    firmwareName.value = updateNamePayload.name;
    firmwareData.name = firmwareName.value ?? firmwareData.name;
    isEditingName.value = false;
    updateEdited(firmwareData);
  }
  isSavingName.value = false;
};

const onValidateEditDescription = async (newValue: string) => {
  const updateDescriptionPayload: FirmwareUpdatePayload = {
    description: newValue,
  };
  isSavingDescription.value = true;
  const isUpdateSucceed = await onValidate(updateDescriptionPayload);
  if (isUpdateSucceed) {
    firmwareDescription.value = updateDescriptionPayload.description;
    firmwareData.description = firmwareDescription.value;
    isEditingDescription.value = false;
    updateEdited(firmwareData);
  }
  isSavingDescription.value = false;
};

const onValidate = async (
  firmwareUpdatePayload: FirmwareUpdatePayload,
): Promise<boolean> => {
  if (props.uuid) {
    await updateFirmwareNameAndDescription(props.uuid, firmwareUpdatePayload);

    if (errorUpdateFirmwareNameAndDescription.value) {
      errorUpdateFirmwareNameAndDescription.value.notify(NotificationKey.error);
      return false;
    } else {
      showNotificationSuccess(t(NotificationKey.success));
      return true;
    }
  }
  return false;
};

watch(() => props.uuid, getFirmwareByUuid);
watch(() => firmwareData, updateEdited, { deep: true });
</script>

<template>
  <lxc-side-canvas
    v-model:show="show"
    size="1/3"
    :header="firmwareData.name"
    :close-tooltip="t('button.close')"
    body-class="!pt-2 !pb-4 bg-white"
    @discard="close"
  >
    <container-component
      :is-loading="isLoading"
      :error="error"
      :px="0"
      :py="0"
      class="bg-white"
    >
      <section :class="sectionClass">
        <firmware-side-section-header>
          {{ t("firmware.section.informations.title") }}
        </firmware-side-section-header>
        <editable-field
          v-if="canUpdateFirmware"
          v-model:is-editing="isEditingName"
          :field-value="firmwareName"
          :label="t('firmware.form.name')"
          form-item-prop="name"
          :rules="firmwareNameUpdateFormRules"
          :is-saving="isSavingName"
          buttons-on-side
          is-read-only
          @validate="onValidateEditName"
        />
        <div v-else class="mt-4">
          <div :class="labelClass">
            {{ t("firmware.form.name") }}
          </div>
          <div :class="valueClass">
            {{ firmwareName }}
          </div>
        </div>
        <div :class="labelClass">
          {{ t("firmware.form.version") }}
        </div>
        <div :class="valueClass">
          {{ firmwareData.version }}
        </div>
      </section>

      <section :class="sectionClass">
        <firmware-side-section-header>
          {{ t("firmware.section.compatibility.title") }}
        </firmware-side-section-header>
        <div :class="labelClass">
          {{ t("firmware.form.range") }}
        </div>
        <div :class="valueClass">
          {{ firmwareData.rangeFriendlyName }}
        </div>
        <div v-if="firmwareData.declination" :class="labelClass">
          {{ t("firmware.form.declination") }}
        </div>
        <div v-if="firmwareData.declination" :class="valueClass">
          {{ firmwareData.declination }}
        </div>
        <div :class="labelClass">
          {{ t("firmware.form.hardwareVersions") }}
        </div>
        <firmware-property-list
          :items="firmwareData.hardwareVersions"
          type="primary"
        />
        <div :class="labelClass">
          {{ t("firmware.form.firmwareVersions") }}
        </div>
        <firmware-property-list
          :items="firmwareData.firmwareVersions"
          type="primary"
        />
      </section>

      <section :class="sectionClass">
        <firmware-side-section-header>
          {{ t("firmware.section.complements.title") }}
        </firmware-side-section-header>
        <div :class="labelClass">
          {{ t("firmware.form.status") }}
        </div>
        <div :class="valueClass">
          <firmware-status-badge :status="firmwareData.status" />
        </div>
        <editable-field
          v-if="canUpdateFirmware"
          v-model:is-editing="isEditingDescription"
          :field-value="firmwareDescription"
          class="mt-4"
          :label="t('firmware.form.description')"
          form-item-prop="description"
          :is-saving="isSavingDescription"
          buttons-on-side
          is-read-only
          @validate="onValidateEditDescription"
        />
        <div v-else class="mt-4">
          <div :class="labelClass">
            {{ t("firmware.form.description") }}
          </div>
          <div :class="valueClass">
            {{ firmwareDescription }}
          </div>
        </div>
      </section>
      <div v-if="canUpdateFirmware" class="mt-8">
        <lxc-button
          html-type="button"
          type="danger"
          :disabled="
            isLoadingDeleteFirmware ||
            isLoadingActivateFirmware ||
            isLoadingDeactivateFirmware
          "
          :icon="!isLoadingDeleteFirmware ? ILxcLightTrash2 : undefined"
          :title="t('firmware.actions.delete.label')"
          class="whitespace-nowrap"
          @click="toggleDeleteConfirmVisible(true)"
        >
          <div
            v-if="isLoadingDeleteFirmware"
            class="inline-block align-middle relative"
          >
            <lxc-loader />
          </div>
          {{ t("firmware.actions.delete.label") }}
        </lxc-button>
      </div>
    </container-component>

    <template #footer>
      <div class="grid grid-cols-[max-content_auto] gap-4">
        <lxc-button
          html-type="button"
          type="secondary"
          :title="t('button.close')"
          @click="close"
        >
          {{ t("button.close") }}
        </lxc-button>

        <lxc-button
          v-if="canUpdateFirmware && !error && activateFirmwareVisible"
          html-type="button"
          type="primary"
          :disabled="
            isLoadingActivateFirmware ||
            isLoadingDeactivateFirmware ||
            isLoadingDeleteFirmware
          "
          :icon="!isLoadingActivateFirmware ? ILxcLightRotateCw : undefined"
          :title="t('firmware.actions.activate.label')"
          class="whitespace-nowrap"
          @click="confirmActivateFirmware"
        >
          <div
            v-if="isLoadingActivateFirmware"
            class="inline-block align-middle relative"
          >
            <lxc-loader />
          </div>
          {{ t("firmware.actions.activate.label") }}
        </lxc-button>

        <lxc-button
          v-if="canUpdateFirmware && !error && deactivateFirmwareVisible"
          html-type="button"
          type="secondary"
          :disabled="
            isLoadingDeactivateFirmware ||
            isLoadingActivateFirmware ||
            isLoadingDeleteFirmware
          "
          :icon="!isLoadingDeactivateFirmware ? ILxcLightSlash : undefined"
          :title="t('firmware.actions.deactivate.label')"
          class="whitespace-nowrap"
          @click="confirmDeactivateFirmware"
        >
          <div
            v-if="isLoadingDeactivateFirmware"
            class="inline-block align-middle relative"
          >
            <lxc-loader />
          </div>
          {{ t("firmware.actions.deactivate.label") }}
        </lxc-button>
      </div>
    </template>
  </lxc-side-canvas>

  <confirm-modal
    :is-dialog-visible="deleteConfirmDialogVisible"
    :title="t('firmware.actions.delete.label')"
    :description="
      t('firmware.actions.delete.description', { name: firmwareData.name })
    "
    @cancel="toggleDeleteConfirmVisible"
    @confirm="onDeleteFirmwareAndPropagateChange"
  />

  <confirm-modal
    :is-dialog-visible="activateConfirmDialogVisible"
    :title="t('firmware.actions.activate.label')"
    :description="
      t('firmware.actions.activate.description', { name: firmwareData.name })
    "
    @cancel="toggleActivateConfirmVisible"
    @confirm="onActivateFirmware"
  />

  <confirm-modal
    :is-dialog-visible="deactivateConfirmDialogVisible"
    :title="t('firmware.actions.deactivate.label')"
    :description="
      t('firmware.actions.deactivate.description', { name: firmwareData.name })
    "
    @cancel="toggleDeactivateConfirmVisible"
    @confirm="onDeactivateFirmware"
  />
</template>
